<script setup lang="ts">
import api from "@/api";
import { getSessionStorageReac } from "@/assets/js/helpers";
import { useDefaultStore, type TSinglePlayerGameType } from "@/store";
import { useToast } from "primevue/usetoast";

type TPerson = {
	birth_date?: string | null;
	id: string | number;
	name?: string | null;
	poster_path?: string | null;
	profile_path?: string | null;
	title?: string | null;
};

type TFeedbackState = "idle" | "loading" | "correct" | "wrong";

const props = defineProps<{
	gameType: TSinglePlayerGameType;
}>();

const GAME_TOKEN_KEY = "game-token";
const store = useDefaultStore();
const router = useRouter();
const toast = useToast();

const people = ref<TPerson[]>([]);
const isSubmitting = ref(false);
const isLocked = ref(false);
const isGameOver = ref(false);
const isExpiredGameOver = ref(false);
const currentRound = ref(1);
const score = ref(0);
const feedbackState = ref<TFeedbackState>("idle");
const peopleVisible = ref(true);
const timeLeft = ref(0);
let timerInterval: ReturnType<typeof setInterval> | null = null;

const gameToken = getSessionStorageReac<string | null>(
	GAME_TOKEN_KEY,
	false,
	null,
);

const isBeatTheClock = computed(
	() => props.gameType === store.SINGLE_PLAYER_GAME_TYPES.BEAT_THE_CLOCK,
);

function sleep(ms: number) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

function getPersonName(person: TPerson) {
	return person.name || person.title || "Unknown person";
}

function getPersonImage(person: TPerson) {
	const path = person.profile_path || person.poster_path || "";

	if (!path) return "";
	if (/^(https?:)?\/\//.test(path) || path.startsWith("data:")) return path;

	return `https://image.tmdb.org/t/p/original${path}`;
}

function getPanelStyle(person: TPerson) {
	const image = getPersonImage(person);

	return {
		backgroundImage: image ? `url("${image}")` : "none",
	};
}

function clearTimer() {
	if (timerInterval) {
		clearInterval(timerInterval);
		timerInterval = null;
	}

	timeLeft.value = 0;
}

function startTimer(seconds: number) {
	clearTimer();
	timeLeft.value = Math.max(0, Math.ceil(seconds));

	if (timeLeft.value <= 0) {
		expireGame();
		return;
	}

	timerInterval = setInterval(() => {
		if (timeLeft.value > 0) {
			timeLeft.value -= 1;
		}

		if (timeLeft.value <= 0) {
			expireGame();
		}
	}, 1000);
}

function expireGame() {
	isGameOver.value = true;
	isExpiredGameOver.value = true;
	isLocked.value = true;
	feedbackState.value = "wrong";
	clearTimer();
}

function resetLocalState() {
	people.value = [];
	isSubmitting.value = false;
	isLocked.value = false;
	isGameOver.value = false;
	isExpiredGameOver.value = false;
	currentRound.value = 1;
	score.value = 0;
	feedbackState.value = "idle";
	peopleVisible.value = true;
	clearTimer();
}

async function playAgain() {
	gameToken.value = null;
	resetLocalState();
	await getInitialPair();
}

async function getInitialPair() {
	try {
		const res = await api.getPeopleInitialPair({
			token: store.userData?.usr_token ?? "",
			type: props.gameType,
		});
		const data = res.data.data;

		if (!data?.ok || !Array.isArray(data.people)) {
			await router.push({ name: "PeoplePage" });
			return;
		}

		gameToken.value = data.token ?? null;
		people.value = data.people;
		currentRound.value = 1;
		score.value = data.score ?? 0;

		if (isBeatTheClock.value && typeof data.secondsLeft === "number") {
			startTimer(data.secondsLeft);
		}
	} catch (err: any) {
		console.warn(err.message);
		toast.add({
			severity: "error",
			summary: "Error",
			detail: err?.response?.data?.data?.showText
				? err.response.data.data.message
				: null,
			life: 3500,
		});
		await router.push({ name: "PeoplePage" });
	}
}

async function getPeopleSinglePlayerState(currentGameToken: string) {
	try {
		const res = await api.getPeopleSinglePayerState({
			token: currentGameToken,
			type: props.gameType,
		});
		const data = res.data.data;

		if (!data?.ok || !Array.isArray(data.people)) {
			gameToken.value = null;
			await getInitialPair();
			return;
		}

		people.value = data.people;
		currentRound.value = data.round ?? 1;
		score.value = data.score ?? 0;

		if (isBeatTheClock.value && typeof data.secondsLeft === "number") {
			startTimer(data.secondsLeft);
		}

		if (data.isFinished || data.secondsLeft === 0) {
			expireGame();
		}
	} catch (err: any) {
		console.warn(err.message);
		gameToken.value = null;
		await getInitialPair();
	}
}

async function initializeGame() {
	if (gameToken.value) {
		await getPeopleSinglePlayerState(gameToken.value);
		return;
	}

	await getInitialPair();
}

async function animateTransition(nextPeople: TPerson[], state: "correct" | "wrong") {
	feedbackState.value = state;

	await sleep(500);
	peopleVisible.value = false;

	await sleep(260);
	people.value = nextPeople;
	currentRound.value += 1;

	await nextTick();
	peopleVisible.value = true;

	await sleep(260);

	if (!isGameOver.value) {
		feedbackState.value = "idle";
	}
}

async function handleAnswer(clickedIndex: 0 | 1) {
	if (people.value.length !== 2 || isSubmitting.value || isLocked.value) {
		return;
	}

	try {
		isSubmitting.value = true;
		feedbackState.value = "loading";

		const request = isBeatTheClock.value
			? api.postPeopleBTCHandleAnswer({
					vote: people.value[clickedIndex].id,
					token: gameToken.value ?? "",
				})
			: api.postPeopleHandleAnswer({
					vote: people.value[clickedIndex].id,
					token: gameToken.value ?? "",
					type: props.gameType,
				});

		const res = await request;
		const data = res.data.data;

		if (!data?.ok) {
			feedbackState.value = "idle";
			return;
		}

		score.value = data.score ?? score.value;

		if (Array.isArray(data.people) && data.people.length === 2) {
			await animateTransition(data.people, data.isCorrect ? "correct" : "wrong");
			return;
		}

		if (!data.isCorrect || !isBeatTheClock.value) {
			isLocked.value = true;
			isGameOver.value = true;
			feedbackState.value = "wrong";
			clearTimer();
			return;
		}

		feedbackState.value = "idle";
	} catch (err: any) {
		console.warn(err.message);
		feedbackState.value = "idle";
		toast.add({
			severity: "error",
			summary: "Error",
			detail: err?.response?.data?.data?.showText
				? err.response.data.data.message
				: null,
			life: 3500,
		});
	} finally {
		isSubmitting.value = false;
	}
}

onMounted(() => {
	initializeGame();
});

onBeforeUnmount(() => {
	clearTimer();
});
</script>

<template>
	<section
		class="people-game-page"
		:class="{
			'people-game-page--correct': feedbackState === 'correct',
			'people-game-page--wrong': feedbackState === 'wrong',
		}"
	>
		<RouterLink
			:to="{ name: 'PeoplePage' }"
			class="category-back-link"
		>
			← Back
		</RouterLink>

		<div class="round-badge">
			{{ isBeatTheClock ? `${score} pts` : `Round ${currentRound}` }}
		</div>
		<div
			v-if="isBeatTheClock"
			class="timer-badge"
			:class="{ 'is-low': timeLeft <= 10 }"
		>
			{{ timeLeft }}s
		</div>

		<div
			v-if="currentRound === 1"
			class="instruction-badge"
		>
			Who was born earlier?
		</div>

		<div
			v-if="people.length === 2"
			class="or-badge"
			:class="{
				'is-loading': feedbackState === 'loading',
				'is-correct': feedbackState === 'correct',
				'is-wrong': feedbackState === 'wrong',
			}"
		>
			<span v-if="feedbackState === 'correct'">✓</span>
			<span v-else-if="feedbackState === 'wrong'">✕</span>
			<span v-else-if="feedbackState === 'loading'">...</span>
			<span v-else>OR</span>
		</div>

		<div
			v-if="
				people.length === 2 &&
				((isBeatTheClock && isExpiredGameOver) ||
					(!isBeatTheClock && isGameOver))
			"
			class="wrong-actions"
		>
			<button
				type="button"
				class="play-again-button"
				aria-label="Play again"
				title="Play again"
				@click="playAgain"
			>
				<i-fa-rotate-right
					class="play-again-button__icon"
					aria-hidden="true"
				/>
			</button>
		</div>

		<div
			v-if="people.length === 2"
			class="people-panels"
		>
			<button
				v-for="(person, index) in people"
				:key="`${person.id}-${index}`"
				type="button"
				class="person-panel"
				:class="{
					'is-disabled': isSubmitting || isLocked,
					'is-fading': !peopleVisible,
				}"
				:style="getPanelStyle(person)"
				:disabled="isSubmitting || isLocked"
				@click="handleAnswer(index as 0 | 1)"
			>
				<span class="person-panel__overlay"></span>
				<span class="person-panel__content">
					<span class="person-name">{{ getPersonName(person) }}</span>
				</span>
			</button>
		</div>

		<div
			v-else
			class="people-loading"
		>
			<ProgressSpinner
				style="width: 48px; height: 48px"
				stroke-width="8"
				fill="transparent"
				animation-duration=".5s"
			/>
		</div>
	</section>
</template>

<style scoped lang="scss">
.people-game-page {
	position: relative;
	display: grid;
	grid-template-columns: 1fr;
	grid-template-rows: 1fr 1fr;
	width: 100%;
	min-height: 100dvh;
	background: #111;
	color: #fff;
	overflow: hidden;
}

.people-panels {
	display: contents;
}

.person-panel {
	position: relative;
	min-height: 50dvh;
	border: 0;
	background-color: #1b1b1b;
	background-position: center;
	background-size: cover;
	color: inherit;
	cursor: pointer;
	overflow: hidden;
	opacity: 1;
	transform: scale(1);
	transition:
		filter 0.25s ease,
		opacity 0.25s ease,
		transform 0.25s ease;
}

.person-panel:hover:not(.is-disabled) {
	.person-panel__overlay {
		background: rgba(0, 0, 0, 0.28);
	}
}

.person-panel.is-disabled {
	cursor: default;
}

.person-panel.is-fading {
	opacity: 0;
	transform: scale(1.025);
}

.person-panel__overlay {
	position: absolute;
	inset: 0;
	background: rgba(0, 0, 0, 0.45);
	transition:
		background 0.25s ease,
		opacity 0.25s ease;
}

.people-game-page--correct .person-panel__overlay {
	background: rgba(20, 70, 34, 0.48);
}

.people-game-page--wrong .person-panel__overlay {
	background: rgba(95, 20, 20, 0.5);
}

.person-panel__content {
	position: relative;
	z-index: 1;
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	text-align: center;
}

.person-name {
	display: block;
	font-size: clamp(28px, 5vw, 64px);
	font-weight: 800;
	line-height: 1.1;
	text-shadow: 0 4px 20px rgba(0, 0, 0, 0.45);
	user-select: none;
}

.category-back-link,
.round-badge,
.timer-badge,
.instruction-badge,
.or-badge,
.wrong-actions {
	position: absolute;
	z-index: 5;
}

.category-back-link {
	top: 18px;
	left: 18px;
	color: #fff;
	text-decoration: none;
	font-weight: 800;
}

.round-badge {
	top: 16px;
	right: 16px;
	padding: 8px 12px;
	border-radius: 999px;
	background: rgba(0, 0, 0, 0.22);
	color: rgba(255, 255, 255, 0.78);
	font-size: 14px;
	font-weight: 600;
	backdrop-filter: blur(6px);
	pointer-events: none;
}

.timer-badge {
	top: 56px;
	left: 50%;
	transform: translateX(-50%);
	padding: 8px 14px;
	border-radius: 999px;
	background: rgba(0, 0, 0, 0.58);
	font-weight: 900;
}

.timer-badge.is-low {
	color: #ffb6b6;
}

.instruction-badge {
	left: 50%;
	top: calc(50% - 112px);
	transform: translateX(-50%);
	padding: 6px 10px;
	border-radius: 999px;
	background: rgba(0, 0, 0, 0.18);
	color: rgba(255, 255, 255, 0.62);
	font-size: 10px;
	font-weight: 500;
	line-height: 1.2;
	text-align: center;
	backdrop-filter: blur(6px);
	max-width: calc(100% - 32px);
	pointer-events: none;
}

.or-badge {
	left: 50%;
	top: 50%;
	transform: translate(-50%, -50%) scale(1);
	display: flex;
	align-items: center;
	justify-content: center;
	width: 84px;
	height: 84px;
	border-radius: 50%;
	background: #fff;
	color: rgb(17, 17, 17);
	font-size: 24px;
	font-weight: 900;
	box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
	pointer-events: none;
	transition:
		background 0.25s ease,
		color 0.25s ease,
		transform 0.25s ease,
		box-shadow 0.25s ease,
		opacity 0.25s ease;
}

.or-badge.is-loading {
	transform: translate(-50%, -50%) scale(0.96);
}

.or-badge.is-correct {
	background: rgb(49, 180, 90);
	color: rgb(255, 255, 255);
	transform: translate(-50%, -50%) scale(1.08);
	box-shadow: 0 12px 36px rgba(49, 180, 90, 0.35);
}

.or-badge.is-wrong {
	background: rgb(208, 62, 62);
	color: rgb(255, 255, 255);
	transform: translate(-50%, -50%) scale(1.08);
	box-shadow: 0 12px 36px rgba(208, 62, 62, 0.35);
}

.wrong-actions {
	left: 50%;
	bottom: 28px;
	transform: translateX(-50%);
}

.play-again-button {
	border: 1px solid rgba(255, 255, 255, 0.14);
	border-radius: 50%;
	width: 42px;
	height: 42px;
	padding: 0;
	background: rgba(255, 255, 255, 0.14);
	color: rgb(255, 255, 255);
	cursor: pointer;
	backdrop-filter: blur(8px);
	box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	transition:
		background 0.2s ease,
		border-color 0.2s ease,
		transform 0.2s ease;
}

.play-again-button:hover {
	background: rgba(255, 255, 255, 0.2);
	border-color: rgba(255, 255, 255, 0.24);
	transform: translateY(-1px);
}

.play-again-button__icon {
	width: 20px;
	height: 20px;
	display: block;
}

.people-loading {
	min-height: 100dvh;
	display: grid;
	place-items: center;
}

@media (max-width: 740px) {
	.people-panels {
		display: contents;
	}

	.person-panel__content {
		padding: 20px;
	}
}

@media (min-width: 768px) {
	.people-game-page {
		grid-template-columns: 1fr 1fr;
		grid-template-rows: 1fr;
	}

	.person-panel {
		min-height: 100dvh;
	}

	.category-back-link {
		top: 20px;
		left: 20px;
	}

	.round-badge {
		top: 20px;
		right: 20px;
	}

	.timer-badge {
		top: 72px;
	}

	.instruction-badge {
		top: 20px;
		left: 50%;
		transform: translateX(-50%);
		max-width: min(440px, calc(100% - 180px));
		font-size: 11px;
		padding: 6px 12px;
	}

	.wrong-actions {
		top: 50%;
		left: calc(50% + 110px);
		bottom: auto;
		transform: translateY(-50%);
		justify-content: flex-start;
	}
}
</style>
